/**
 * cePublic token freshness.
 *
 * The site signs every visitor in automatically with a cePublic Cognito ID
 * token (getCeToken), mirrored to localStorage.token. Whether that token is
 * still usable is decided from its own `exp` claim. The old check trusted a
 * separate lastTimestamp written beside it, and once the two drifted apart
 * (laptop sleep, throttled background timers, another tab or an embed iframe
 * on the same origin writing `token`) the site kept sending an expired token
 * that it believed was fresh, and stayed broken until storage was cleared.
 *
 * installCeTokenRefresh adds two axios interceptors, so every axios caller is
 * covered, including the components that read localStorage.token themselves:
 *   - before a request: a cePublic token that has expired, or is about to, is
 *     swapped for a fresh one;
 *   - after an auth failure: the token is refreshed once and the request is
 *     retried with it.
 * Concurrent callers share a single refresh.
 */
import Config from "../config";
import { isEmbedMode } from "../modules/selfService/embed/embedParams";
import { decodeGuestJwt } from "./guestAuth";

// Refresh this long before `exp`, so a request does not set off with a token
// that expires on the way.
export const CE_TOKEN_EXPIRY_MARGIN_MS = 5 * 60 * 1000;

const readStoredToken = () => {
  try {
    return localStorage.getItem("token");
  } catch (e) {
    return null;
  }
};

export const getCeTokenExpiry = (token) => {
  const exp = decodeGuestJwt(token)?.exp;
  return typeof exp === "number" ? exp * 1000 : null;
};

export const isCeTokenFresh = (token, now = Date.now()) => {
  const expiry = getCeTokenExpiry(token);
  return expiry !== null && expiry - now > CE_TOKEN_EXPIRY_MARGIN_MS;
};

// Only Cognito-issued tokens are managed here. Guest JWTs are signed by the
// backend and have their own session handling (utils/guestAuth).
const isCognitoToken = (token) =>
  /cognito-idp\./.test(decodeGuestJwt(token)?.iss || "");

// The API reports a bad or expired token as 401/403, or as 400 "Invalid Token"
// (utils.decodeToken in ce-asset). Anything else (a 500, a network error, a
// malformed request) is not an auth problem and must not rotate the token.
export const isAuthFailure = (error) => {
  const status = error?.response?.status;
  if (status === 401 || status === 403) return true;
  return status === 400 && /invalid token/i.test(error?.response?.data?.message || "");
};

let refreshCeToken = null;
let refreshInFlight = null;

// Runs the registered refresh at most once at a time and resolves with the
// token in storage afterwards (unchanged if the refresh failed).
const refreshOnce = () => {
  if (!refreshCeToken) return Promise.resolve(readStoredToken());
  if (!refreshInFlight) {
    refreshInFlight = Promise.resolve()
      .then(() => refreshCeToken())
      .catch(() => {})
      .then(() => {
        refreshInFlight = null;
        return readStoredToken();
      });
  }
  return refreshInFlight;
};

// The stored token if it is still fresh, otherwise a refreshed one.
export const ensureFreshCeToken = () => {
  const stored = readStoredToken();
  if (isEmbedMode() || isCeTokenFresh(stored)) return Promise.resolve(stored);
  return refreshOnce();
};

const getHeader = (config) => {
  const headers = config.headers || {};
  return typeof headers.get === "function"
    ? headers.get("Authorization")
    : headers.Authorization;
};

const setHeader = (config, value) => {
  if (typeof config.headers?.set === "function") {
    config.headers.set("Authorization", value);
  } else {
    config.headers = { ...(config.headers || {}), Authorization: value };
  }
};

// The cePublic token a request carries, or null when the request is not ours
// to manage: the token call itself, embeds (their origin-scoped token is
// minted by EmbedApp), guest JWTs and unauthenticated calls.
const managedToken = (config) => {
  if (!config || config.url === Config.dev.url.getCeToken || isEmbedMode()) return null;
  const header = getHeader(config);
  if (typeof header !== "string" || !header) return null;
  const token = header.replace(/^Bearer\s+/i, "");
  return isCognitoToken(token) ? token : null;
};

const withToken = (config, token) => {
  const header = getHeader(config);
  setHeader(config, /^Bearer\s+/i.test(header) ? `Bearer ${token}` : token);
  return config;
};

/**
 * Registers how to fetch a new token and wires the interceptors into the
 * given axios instance. `refresh` must store the new token in
 * localStorage.token; its result is not used.
 */
export const installCeTokenRefresh = (axiosInstance, refresh) => {
  refreshCeToken = refresh;

  const requestId = axiosInstance.interceptors.request.use(async (config) => {
    const token = managedToken(config);
    if (!token || isCeTokenFresh(token)) return config;
    const fresh = await ensureFreshCeToken();
    return fresh && fresh !== token ? withToken(config, fresh) : config;
  });

  const responseId = axiosInstance.interceptors.response.use(undefined, async (error) => {
    const config = error?.config;
    const token = managedToken(config);
    if (!token || !isAuthFailure(error)) throw error;
    // Tell fetchData not to refresh again for the same failure.
    error.ceTokenRefreshHandled = true;
    if (config.ceTokenRetried) throw error;

    const stored = readStoredToken();
    const fresh = stored && stored !== token && isCeTokenFresh(stored)
      ? stored
      : await refreshOnce();
    if (!fresh || fresh === token) throw error;

    config.ceTokenRetried = true;
    return axiosInstance(withToken(config, fresh));
  });

  return () => {
    axiosInstance.interceptors.request.eject(requestId);
    axiosInstance.interceptors.response.eject(responseId);
    refreshCeToken = null;
    refreshInFlight = null;
  };
};
